import { useState } from 'react';
import { useApiUrl, useCustom } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { ChevronLeft, Save, Loader2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { OPTION_MODES, type Section } from './constants';
import { QuestionForm, emptyQuestionForm, validateQuestionForm, formToBody, type QuestionFormState } from './QuestionForm';

interface DraftOption { option_text: string; option_text_es: string; synonyms: string }

export function QuestionCreatePage() {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const [form, setForm] = useState<QuestionFormState>(emptyQuestionForm);
  const [options, setOptions] = useState<DraftOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: sectionsData } = useCustom<{ sections: Section[] }>({ url: '/v1/admin/sections', method: 'get' });
  const sections = sectionsData?.data?.sections ?? [];

  const usesOptions = OPTION_MODES.includes(form.response_mode as never);
  const validationError = validateQuestionForm(form, 'create');

  const addOption = () => setOptions((o) => [...o, { option_text: '', option_text_es: '', synonyms: '' }]);
  const updateOption = (i: number, patch: Partial<DraftOption>) =>
    setOptions((o) => o.map((opt, idx) => (idx === i ? { ...opt, ...patch } : opt)));
  const removeOption = (i: number) => setOptions((o) => o.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (validationError) { setError(validationError); return; }
    if (usesOptions && options.filter((o) => o.option_text.trim()).length < 2) {
      setError('Select-type questions need at least 2 options'); return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = formToBody(form, 'create');
      if (usesOptions) {
        body.options = options
          .filter((o) => o.option_text.trim())
          .map((o, i) => ({
            option_text: o.option_text.trim(),
            option_text_es: o.option_text_es.trim() || null,
            synonyms: o.synonyms.split(',').map((s) => s.trim()).filter(Boolean),
            display_order: i + 1,
          }));
      }
      const idToken = await getIdToken();
      const r = await fetch(`${apiUrl}/v1/admin/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ message: 'Create failed' }));
        throw new Error((err as { message?: string; error?: string }).message ?? (err as { error?: string }).error ?? `HTTP ${r.status}`);
      }
      toast({ title: 'Question created' });
      navigate('/questions');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <button onClick={() => navigate('/questions')}
          className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Back to questions
        </button>
        <h1 className="text-2xl font-semibold">New question</h1>
        <p className="text-sm text-muted-foreground mt-1">Writes directly to the live onboarding catalog (onboarding.questions).</p>
      </div>

      <QuestionForm value={form} onChange={setForm} sections={sections} mode="create" />

      {usesOptions && (
        <div className="mt-6 border-t pt-5">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-medium">Options</h2>
            <button onClick={addOption} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add option
            </button>
          </div>
          {options.length === 0 && <p className="text-xs text-muted-foreground">No options yet — add at least 2.</p>}
          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex gap-2 items-start">
                <span className="text-xs text-muted-foreground pt-2.5 w-5">{i + 1}.</span>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input type="text" value={opt.option_text} placeholder="Label (EN)"
                    onChange={(e) => updateOption(i, { option_text: e.target.value })}
                    className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  <input type="text" value={opt.option_text_es} placeholder="Label (ES)"
                    onChange={(e) => updateOption(i, { option_text_es: e.target.value })}
                    className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  <input type="text" value={opt.synonyms} placeholder="synonyms, comma-separated"
                    onChange={(e) => updateOption(i, { synonyms: e.target.value })}
                    className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <button onClick={() => removeOption(i)} className="p-2 text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm mt-5">
          <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
          <div className="text-destructive">{error}</div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-5 mt-5 border-t">
        <button type="button" onClick={() => navigate('/questions')} disabled={submitting}
          className="px-4 py-2 border rounded-md text-sm hover:bg-muted/50 disabled:opacity-50">Cancel</button>
        <button type="button" disabled={!!validationError || submitting} onClick={handleSubmit}
          className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {submitting ? 'Creating…' : 'Create question'}
        </button>
      </div>
    </div>
  );
}
